import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  INVESTOR_LIST_COLUMNS,
  isAggregatesOnlyAccess,
  isCrmReferrerViewEnabled,
  kycBadge,
  presentColumns,
} from "../lib/crmInvestors";
import { Users, Layers, ShieldCheck, RefreshCw } from "lucide-react";

/**
 * MyInvestorsPage — the referrer's "My investors" list on the EchoSense
 * CRM (STARSEED-CRM-011).
 *
 * Data path: capmarkets-api proxy (/me/crm/*) → 008 bridge → platform
 * /v1/crm/*. The platform's persona field redaction decides what comes
 * back; this page renders ONLY what arrives (lib/crmInvestors.js rule 1)
 * and scaffolds nothing for hidden fields (rule 2).
 *
 * Access states:
 * - tier-1 referrer (owns investors): list + aggregates card
 * - tier-2 introducer: the list endpoint 403s → aggregates card ONLY
 * - flag off / unreadable: route gate bounces; belt-and-braces check here
 */

/* Tier-2 aggregates card — counts + pipeline-stage rollups, visually in
   line with the introducer-stats stat-tile presentation. */
export const CrmAggregatesCard = ({ aggregates }) => {
  if (!aggregates) return null;
  const stageRollup = aggregates.stageRollup || [];
  const kycRollup = aggregates.kycRollup || [];
  const kycPass = kycRollup.find((r) => r.kycStatus === "pass")?.count || 0;
  return (
    <Card className="bg-surface border-white/5" data-testid="crm-aggregates-card">
      <CardHeader>
        <CardTitle className="text-white font-serif flex items-center gap-2">
          <Layers className="text-gold" size={20} />
          EchoSense CRM — Pipeline Overview
        </CardTitle>
        <CardDescription>Investor counts and pipeline-stage rollups</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded bg-obsidian border border-white/5">
            <div className="text-3xl font-bold text-gold tabular-nums">{aggregates.totalParties ?? 0}</div>
            <p className="text-sm text-muted-foreground mt-1">Investors</p>
          </div>
          <div className="p-4 rounded bg-obsidian border border-white/5">
            <div className="text-3xl font-bold text-emerald tabular-nums">{kycPass}</div>
            <p className="text-sm text-muted-foreground mt-1">KYC Passed</p>
          </div>
        </div>
        {stageRollup.length > 0 && (
          <div className="space-y-2">
            {stageRollup.map((row) => (
              <div
                key={row.stage}
                className="flex items-center justify-between p-3 rounded bg-obsidian border border-white/5"
              >
                <span className="text-white capitalize">{String(row.stage).replaceAll("_", " ")}</span>
                <span className="text-gold font-bold tabular-nums">{row.count}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MyInvestorsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [parties, setParties] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  // 'loading' | 'full' | 'aggregates-only' | 'unavailable'
  const [access, setAccess] = useState("loading");

  const fetchData = useCallback(async () => {
    setAccess("loading");
    try {
      const aggRes = await api.get("/me/crm/aggregates");
      setAggregates(aggRes.data?.aggregates || null);
    } catch {
      // Aggregates failing alone doesn't block the list attempt.
      setAggregates(null);
    }
    try {
      const listRes = await api.get("/me/crm/investors");
      setParties(listRes.data?.parties || []);
      setAccess("full");
    } catch (error) {
      if (isAggregatesOnlyAccess(error)) {
        setAccess("aggregates-only"); // tier-2 standing (contract §3)
      } else {
        setAccess("unavailable");
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Belt-and-braces behind the route gate (fail-closed launch flag).
  if (!isCrmReferrerViewEnabled(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const rows = parties || [];
  const columns = presentColumns(INVESTOR_LIST_COLUMNS, rows);

  return (
    <DashboardLayout title="My Investors" subtitle="Your investor book on the EchoSense CRM">
      {access === "loading" && (
        <div className="animate-pulse space-y-6">
          <div className="h-48 bg-surface rounded" data-testid="crm-loading" />
        </div>
      )}

      {access === "unavailable" && (
        <Card className="bg-surface border-white/5" data-testid="crm-unavailable">
          <CardContent className="p-8 text-center text-gray-400">
            <RefreshCw className="mx-auto mb-3 opacity-50" size={32} />
            <p>EchoSense CRM is temporarily unavailable.</p>
            <button
              type="button"
              onClick={fetchData}
              className="mt-3 text-gold hover:underline text-sm"
              data-testid="crm-retry"
            >
              Try again
            </button>
          </CardContent>
        </Card>
      )}

      {access === "aggregates-only" && (
        <div className="space-y-6" data-testid="crm-tier2-view">
          <CrmAggregatesCard aggregates={aggregates} />
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <ShieldCheck size={16} className="text-gold shrink-0" />
            Your introducer standing provides aggregate pipeline visibility. Per-investor detail is
            available to tier-1 introducers for the investors they manage.
          </p>
        </div>
      )}

      {access === "full" && (
        <div className="space-y-6">
          <Card className="bg-surface border-white/5" data-testid="crm-investor-list">
            <CardHeader>
              <CardTitle className="text-white font-serif flex items-center gap-2">
                <Users className="text-gold" size={20} />
                My Investors
              </CardTitle>
              <CardDescription>
                Investors you manage — served by the EchoSense CRM
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="mx-auto mb-3 opacity-50" size={40} />
                  <p>No investors in your book yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-white/5">
                        {columns.map((col) => (
                          <th key={col.key} className="py-2 pr-4 font-medium">
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((party) => (
                        <tr
                          key={party.id}
                          onClick={() => navigate(`/my-investors/${party.id}`)}
                          onKeyDown={(e) => {
                            // Keyboard parity for the row's click affordance
                            // (Copilot review): Enter/Space opens the detail.
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/my-investors/${party.id}`);
                            }
                          }}
                          role="link"
                          tabIndex={0}
                          aria-label={`Open investor ${party.name || party.id}`}
                          className="border-b border-white/5 cursor-pointer hover:bg-white/5 focus:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/60 transition-colors"
                          data-testid={`crm-investor-row-${party.id}`}
                        >
                          {columns.map((col) => {
                            const raw = party[col.key];
                            if (col.key === "kycStatus") {
                              const badge = kycBadge(raw);
                              return (
                                <td key={col.key} className="py-3 pr-4">
                                  {badge ? <Badge className={badge.cls}>{badge.label}</Badge> : <span className="text-gray-600">—</span>}
                                </td>
                              );
                            }
                            const value = col.format ? col.format(raw) : raw;
                            return (
                              <td key={col.key} className="py-3 pr-4 text-white">
                                {value ?? <span className="text-gray-600">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <CrmAggregatesCard aggregates={aggregates} />

          <p className="text-xs text-gray-600">
            Data served by the EchoSense CRM.{" "}
            <Link to="/dashboard" className="text-gold hover:underline">
              Back to dashboard
            </Link>
          </p>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MyInvestorsPage;
